import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IL10nString, IL10nUrl } from '../api/domain';

export const isL10Url = (t: any): t is IL10nUrl => typeof t === 'object' && 'url' in t;

export const addLanguageCodeToUrl = (language: string, url: string, publicUrl?: string) => {
    if (publicUrl) {
        return url.replace(publicUrl, `${publicUrl}/${language}`);
    }

    return `/${language}${url}`;
};

export const selectTranslationForLanguage = (languageCode: string, translations: IL10nString[]) => {
    const languageMatchingTranslation = translations?.find((translation) => translation.languageCode === languageCode);
    if (!languageMatchingTranslation) {
        // eslint-disable-next-line no-console
        console.error(`No translation for languageCode ${languageCode}. Available other translation: "${translations?.[0]?.translation || '--none--'}"`);

        return '';
    }

    return languageMatchingTranslation.translation;
};

export const selectUrlTranslationForLanguage = (languageCode: string, translations: IL10nUrl[]) => {
    const languageMatchingTranslation = translations?.find((translation) => translation.languageCode === languageCode);
    if (!languageMatchingTranslation) {
        // eslint-disable-next-line no-console
        console.error(`No url for languageCode ${languageCode}. Available other url: "${translations?.[0]?.url || '--none--'}"`);

        return '';
    }

    return languageMatchingTranslation.url;
};

export function selectTranslationObjectForLanguage(languageCode: string, translations: IL10nString[]): IL10nString;
export function selectTranslationObjectForLanguage(languageCode: string, urlTranslations: IL10nUrl[]): IL10nUrl;

export function selectTranslationObjectForLanguage(languageCode: string, translations: IL10nString[] | IL10nUrl[]): IL10nString | IL10nUrl {
    const languageMatchingTranslation = translations.find((translation) => translation.languageCode === languageCode);
    if (!languageMatchingTranslation) {
        const isUrl = translations && isL10Url(translations[0]);

        // eslint-disable-next-line no-console
        console.error(
            `No ${isUrl ? 'url ' : ''}translation for languageCode ${languageCode}. Available other translation: "${
                translations?.[0]?.translation || '--none--'
            }"`,
        );

        if (isUrl) {
            return {
                translation: '',
                url: '',
                languageCode: '',
            };
        }

        return {
            translation: '',
            languageCode: '',
        };
    }

    return languageMatchingTranslation;
}

export const getTranslationSelector = (languageCode: string) => selectTranslationForLanguage.bind(undefined, languageCode);
export const getUrlTranslationSelector = (languageCode: string) => selectUrlTranslationForLanguage.bind(undefined, languageCode);

export function useSelectedTranslation(translations: IL10nUrl[]): string;
export function useSelectedTranslation(translations: IL10nString[]): string;

export function useSelectedTranslation(translations: IL10nString[], languageCode?: string): string {
    const { i18n } = useTranslation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => selectTranslationForLanguage(languageCode || i18n.language, translations), [languageCode || i18n.language, translations]);
}

export function useSelectedTranslationUrl(translations: IL10nUrl[], languageCode?: string): string {
    const { i18n } = useTranslation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => selectUrlTranslationForLanguage(languageCode || i18n.language, translations), [languageCode || i18n.language, translations]);
}

export function useSelectedTranslationObject(translations: IL10nUrl[]): IL10nUrl;
export function useSelectedTranslationObject(translations: IL10nString[]): IL10nString;

export function useSelectedTranslationObject(translations: IL10nString[] | IL10nUrl[], languageCode?: string) {
    const { i18n } = useTranslation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => selectTranslationObjectForLanguage(languageCode || i18n.language, translations), [languageCode || i18n.language, translations]);
}

export function useSelectedTranslations(translationsList: IL10nUrl[][]): string[];
export function useSelectedTranslations(translationsList: IL10nString[][]): string[];

export function useSelectedTranslations(translationsList: Array<Array<IL10nString | IL10nUrl>>, languageCode?: string): string[] {
    const { i18n } = useTranslation();

    /* eslint-disable react-hooks/exhaustive-deps */
    return useMemo(
        () => translationsList.map((translations) => selectTranslationForLanguage(languageCode || i18n.language, translations)),
        [languageCode || i18n.language, translationsList],
    );
}

export function useSelectedTranslationUrls(translationsList: IL10nUrl[][], languageCode?: string): string[] {
    const { i18n } = useTranslation();

    /* eslint-disable react-hooks/exhaustive-deps */
    return useMemo(
        () => translationsList.map((translations) => selectUrlTranslationForLanguage(languageCode || i18n.language, translations)),
        [languageCode || i18n.language, translationsList],
    );
    /* eslint-enable react-hooks/exhaustive-deps */
}

export function useSelectedTranslationObjects(translations: IL10nUrl[][]): IL10nUrl[];
export function useSelectedTranslationObjects(translations: IL10nString[][]): IL10nString[];

export function useSelectedTranslationObjects(translationsList: Array<Array<IL10nString | IL10nUrl>>, languageCode?: string): IL10nString[] | IL10nUrl[] {
    const { i18n } = useTranslation();

    /* eslint-disable react-hooks/exhaustive-deps */
    return useMemo(
        () => translationsList.map((translations) => selectTranslationObjectForLanguage(languageCode || i18n.language, translations)),
        [languageCode || i18n.language, translationsList],
    );
    /* eslint-enable react-hooks/exhaustive-deps */
}
